// src/components/landing/SignInDialog.jsx
import React, { useEffect, useRef } from 'react';
import AccountPanel from '../AccountPanel';
import { API_BASE } from '../../utils/apiBase';

/**
 * Sign in or create an account, over the landing page.
 *
 * The form is AccountPanel itself — the same component the dashboard's Login pane
 * uses — rather than a second login form. Validation, the role-driven credential
 * fields, session storage and the `decon:auth` announcement all already live
 * there, and a copy would be a second place for any of them to drift. A
 * successful sign-in announces `decon:auth`, AuthGate switches to the dashboard,
 * and this dialog unmounts with the page.
 *
 * A native <dialog> opened with showModal(): it traps focus, makes the page
 * behind it inert and returns focus to the button that opened it — each of which
 * would otherwise be hand-built and easy to get wrong.
 *
 * It does NOT close on a click outside. Registration here is a long form with no
 * draft saved anywhere, so a stray click on the backdrop threw away every field
 * that had been filled in. Leaving is deliberate: the Close button, or Escape on
 * a form with nothing in it.
 */
export default function SignInDialog({ open, onClose }) {
  const ref = useRef(null);

  /**
   * Has anything been typed in?
   *
   * Read off the dialog's own fields rather than threaded down from the form:
   * AccountPanel owns that state, and a prop chain just to answer "is this worth
   * protecting" would couple the two for no gain. Only <input> is counted —
   * the selects start on a real value (the role is "architect" from the first
   * render), so including them would call an untouched form dirty.
   */
  const typedInto = () => {
    const d = ref.current;
    if (!d) return false;
    return [...d.querySelectorAll('input')].some((el) => String(el.value || '').trim() !== '');
  };

  // Closing deliberately still asks, because the answer is usually "no, I did not
  // mean to lose all of that".
  const closeAsked = () => {
    if (typedInto() && !window.confirm('Discard what you have typed?')) return;
    onClose();
  };

  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) {
      try { d.showModal(); } catch { d.setAttribute('open', ''); }
    } else if (!open && d.open) {
      d.close();
    }
  }, [open]);

  return (
    <dialog
      ref={ref}
      className="lp-dialog"
      aria-labelledby="lp-signin-title"
      onClose={onClose}
      /*
       * Escape closes an EMPTY form and is ignored once something has been typed.
       * A native <dialog> closes on Escape by itself, which on a form this long
       * means one stray key throws away every field. The Close button is always
       * there, and asks first.
       */
      onCancel={(e) => {
        if (!typedInto()) return;        // nothing to lose — let it close
        e.preventDefault();
      }}
    >
      <div className="lp-dialog-head">
        <h2 id="lp-signin-title" className="lp-dialog-title">Sign in to VAUOM</h2>
        <button type="button" className="btn" onClick={closeAsked}>Close</button>
      </div>
      {/* Mounted only while open, so each opening starts from a clean form. */}
      <div className="lp-dialog-body">{open && <AccountPanel apiBase={API_BASE} />}</div>
    </dialog>
  );
}
